import React, { useState } from 'react';

import classNames from 'classnames';
import { CustomIcon } from 'components/Icon/CustomIcon';

import styles from './mbutton.module.css';

type Props = {
  children: React.ReactNode;
  onClick?: () => Promise<unknown> | void;
  type?: 'button' | 'submit' | 'reset';
  variant?: 'text' | 'contained' | 'outlined' | 'gradient' | 'pulseGradient';
  color?: 'blue' | 'orange';
  loading?: boolean;
  disabled?: boolean;
  className?: string;
  asChild?: boolean;
};

export function MButton({
  onClick,
  children,
  type = 'button',
  color = 'blue',
  disabled = false,
  variant = 'contained',
  className,
  asChild = false,
}: Props) {
  const [isLoading, setLoading] = useState(false);

  const onClickHandler = async () => {
    if (typeof onClick === 'function') {
      const callResult = onClick();
      if (callResult && typeof callResult.then === 'function') {
        setLoading(true);
        await callResult;
        setLoading(false);
      }
    }
  };
  const isDisabled = disabled || isLoading;

  const isValidReactElement =
    React.Children.count(children) === 1 && React.isValidElement(children);

  if (asChild && !isValidReactElement && process.env.NODE_ENV !== 'production')
    throw new Error(
      'Button with "asChild" prop should have only one child that is a valid React element'
    );

  const extendedClassName = classNames(styles.button, styles[variant], styles[color], className);

  if (asChild && isValidReactElement) {
    const child = children as React.ReactElement<any>;

    const childContent = isLoading ? (
      <div className={styles.loaderIconWrapper}>
        ,
        <CustomIcon icon="loading" />
      </div>
    ) : (
      child.props.children
    );

    return React.cloneElement(
      child,
      {
        className: classNames(extendedClassName, child.props.className),
        disabled: isDisabled,
        type,
        onClick: onClickHandler,
      },
      childContent
    );
  }

  return (
    <button
      type={type}
      disabled={isDisabled}
      onClick={onClickHandler}
      className={extendedClassName}
    >
      {isLoading ? (
        <div className={styles.loaderIconWrapper}>
          <CustomIcon icon="loading" />
        </div>
      ) : (
        children
      )}
    </button>
  );
}
